<template>
  <div class="input-box" :class="classes" :style="{ margin }">
    <label v-if="label" :for="id" class="input-box__label">{{ label }}</label>

    <div class="input-box__wrapper">
      <input
        ref="input"
        v-bind="{ id, placeholder, value: inputValue, name, type, tabindex, disabled, autocomplete }"
        v-on="listeners"
        :data-test-id="testId"
        :class="{ 'input-box__input--ellipsis': ellipsis }"
        class="input-box__input"
      />

      <label :for="id" class="input-box__iconwrapper" v-if="hasSlotIcon" @click="focus">
        <slot name="icon" />
      </label>

      <span v-if="focusShortcut && size !== 'small' && !inputValue" class="input-box__shortcut" @click="focus">
        <ShortcutInfo :shortcut="focusShortcut" />
      </span>

      <div v-if="clearButton && !!inputValue" class="input-box__clearicon">
        <Btn icon size="small" color="ghost" @click="clear">
          <CloseIcon16 />
        </Btn>
      </div>
    </div>

    <div class="error-container" data-test-id="input-box-error" v-if="error && errorMessage">
      <div class="error">{{ errorMessage }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import CloseIcon16 from '../Icons/CloseIcon16.vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'InputBox',

  components: {
    CloseIcon16,
  },

  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },

    type: {
      type: String,
      required: false,
      default: 'text',
    },

    /**
     * required=false so we can set "null" to reset the input
     */
    modelValue: {
      required: false,
      type: [String, Number],
      default: null,
    },

    placeholder: {
      type: String,
      default: '',
      required: false,
    },

    label: {
      type: String,
      default: null,
      required: false,
    },

    error: {
      type: Boolean,
      default: false,
      required: false,
    },

    errorMessage: {
      type: String,
      required: false,
      default: '',
    },

    tabindex: {
      type: Number,
      default: 0,
      required: false,
    },

    name: {
      type: String,
      required: false,
      default: null,
    },

    autocomplete: {
      type: String,
      default: 'off',
      required: false,
    },

    focusOnMount: {
      type: Boolean,
      default: false,
    },

    blurOnEnterPress: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether the input can be updated from a parent component when it is focussed
     * For example, when editing an existing value, it would be confusing for the user
     * if the value suddenly changed (i.e. another user updated it).
     */
    lockOnFocus: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to scroll to the input when it gets focussed
     */
    scrollOnFocus: {
      type: Boolean,
      default: true,
    },

    color: {
      type: String,
      default: 'grey',
      validator(value) {
        return ['dark', 'ghost', 'grey'].indexOf(value) !== -1;
      },
    },

    size: {
      type: String,
      default: 'medium',
      validator(value) {
        return ['small', 'medium', 'medium-centered', 'medium-tight'].indexOf(value) !== -1;
      },
    },

    ellipsis: {
      type: Boolean,
      default: false,
    },

    clearButton: {
      type: Boolean,
      default: false,
    },

    focusShortcut: {
      type: String,
      default: '',
    },

    margin: {
      type: String,
      default: '',
    },

    preventFocus: {
      type: Boolean,
      default: false,
    },

    testId: {
      type: String,
      default: null,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: { 'update:modelValue': null, change: null, focus: null, blur: null },

  data(): { inputValue: string | number; focussed: boolean; listeners: Record<string, (event?: Event) => void> } {
    return {
      inputValue: '',
      focussed: false,
      listeners: {
        input: this.onInput,

        change: this.onChange,

        keydown: this.onKeydown,

        blur: this.onBlur,

        focus: this.onFocus,
      },
    };
  },

  computed: {
    classes(): Array<string | Record<string, boolean>> {
      return [
        `input-box--color-${this.color}`,
        `input-box--size-${this.size}`,
        { 'input-box--icon': this.hasSlotIcon, 'input-box--error': this.error },
      ];
    },

    hasSlotIcon() {
      return hasSlotContent(this.$slots.icon, {});
    },
  },

  methods: {
    onBlur(event: FocusEvent) {
      this.focussed = false;

      // Blur doesn't bubble, so we need to emit it as a component event
      this.$emit('blur', (event.target as HTMLInputElement).value, event);
    },

    onChange(event: Event) {
      const target = event.target as HTMLInputElement;

      const inputValue = this.type === 'number' ? target.valueAsNumber : target.value;
      this.inputValue = inputValue;

      if (inputValue !== this.modelValue) {
        // Only emit event if value has actually changed
        this.$emit('change', inputValue);
      }
    },

    onFocus(event: FocusEvent) {
      if (this.preventFocus) {
        event.preventDefault();
        (event.target as HTMLInputElement).blur();
      } else {
        this.focussed = true;

        // Blur doesn't bubble, so we need to emit it as a component event
        this.$emit('focus', (event.target as HTMLInputElement).value);
      }
    },

    onInput(event: InputEvent) {
      const target = event.target as HTMLInputElement;

      const inputValue = this.type === 'number' ? target.valueAsNumber : target.value;

      this.inputValue = inputValue;
      this.$emit('update:modelValue', inputValue);
    },

    onKeydown(event: KeyboardEvent) {
      if (event.key === 'Enter' && this.$props.blurOnEnterPress) {
        this.$refs.input.blur();
      }
    },

    focus() {
      (this.$refs.input as HTMLInputElement).focus({ preventScroll: !this.scrollOnFocus });
    },

    /**
     * @public
     */
    selectInput() {
      (this.$refs.input as HTMLInputElement).select();
    },

    clear(event: MouseEvent) {
      // prevent that parent components receive this click
      event.stopPropagation();

      this.inputValue = '';
      this.$emit('update:modelValue', this.inputValue);
    },
  },

  mounted() {
    this.inputValue = this.modelValue;

    if (!this.focusOnMount) return;
    this.focus();
  },

  watch: {
    modelValue(val) {
      if ((this.focussed && this.lockOnFocus) || this.error) {
        // Keep the current input value on outside update if configured
        // Same with error - if the value updates, the error doesn't match the input which
        // would be confusing
        return;
      }

      this.inputValue = val;
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
.input-box {
  position: relative;
  width: 100%;
  color: colors.$color-grey-800;
}

.input-box__wrapper {
  position: relative;
  border-radius: settings.$border-radius;
  display: flex;
}

.input-box__label {
  text-align: left;
  padding: 0 0 8px 0;
  color: colors.$color-grey-800;
  margin: 0;
  display: block;
  cursor: default;

  @include typography.typography-13-medium(160%);
}

.input-box__shortcut {
  position: absolute;
  right: 8px;
  height: 100%;
  display: flex;
  align-items: center;
}

.input-box__iconwrapper {
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.input-box__clearicon {
  position: absolute;
  height: 100%;
  right: 0;
  top: 0;
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: colors.$color-grey-600;
}

.input-box__input {
  border-radius: settings.$border-radius;
  border: none;
  outline: none;
  height: 100%;
  width: 100%;
  transition: all 0.1s ease-in-out;

  &[type='number'] {
    appearance: textfield;
  }

  &[type='number']::-webkit-outer-spin-button,
  &[type='number']::-webkit-inner-spin-button {
    /* display: none; <- Crashes Chrome on hover */
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  // Don't show error outline in FF
  &:invalid {
    box-shadow: none;
  }

  // Don't show password reveal in Edge / IE
  &::-ms-reveal {
    display: none;
  }

  &--ellipsis {
    @include typography.typography-ellipsis;
  }
}

/**
 * Sizes
 */
.input-box--size-small {
  @include typography.typography-13-normal(18px);

  .input-box__wrapper {
    height: 28px;
  }

  .input-box__clearicon {
    height: 28px;
  }

  .input-box__input {
    padding: 0 8px 1px 8px;
  }
}

.input-box--size-medium {
  @include typography.typography-14-normal(160%);

  .input-box__wrapper {
    height: 32px;
  }

  .input-box__clearicon {
    height: 32px;
  }

  .input-box__input {
    padding: 0 12px 1px 12px;
  }

  &.input-box--icon {
    .input-box__iconwrapper {
      width: 32px;
    }

    .input-box__clearicon {
      width: 32px;
    }

    .input-box__input {
      padding-left: 32px;
      padding-right: 32px;
    }
  }
}

.input-box--size-medium-centered {
  @extend .input-box--size-medium;

  width: 40px;

  .input-box__input {
    text-align: center;
  }
}

.input-box--size-medium-tight {
  @include typography.typography-14-normal(160%);

  .input-box__wrapper {
    height: 32px;
  }

  .input-box__clearicon {
    height: 32px;
  }

  .input-box__input {
    padding: 0 12px 1px 12px;
  }

  &.input-box--icon {
    .input-box__iconwrapper {
      width: 40px;
    }

    .input-box__clearicon {
      width: 32px;
    }

    .input-box__input {
      padding-left: 38px;
      padding-right: 32px;
    }
  }
}

/**
 * Colors
 */

/**
 * color grey
 */
.input-box--color-grey .input-box__wrapper {
  .input-box__input {
    color: colors.$color-grey-800;
    background: colors.$color-grey-100;
    box-shadow: settings.$box-shadow-border-grey-50;

    &::placeholder {
      color: colors.$color-placeholder;
    }
  }

  // default (with placeholder)
  .input-box__input:placeholder-shown ~ .input-box__iconwrapper {
    color: colors.$color-grey-400;
  }

  // typing, filled
  .input-box__input:not(:placeholder-shown):not(:focus-within),
  .input-box__input:not(:placeholder-shown):not(:focus-within) ~ .input-box__iconwrapper {
    color: colors.$color-grey-600;
  }

  &:hover {
    .input-box__input:not(:disabled) {
      background: colors.$color-white;
    }
  }

  &:focus-within {
    .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
      color: colors.$color-grey-600;
    }

    .input-box__input:not(:disabled) {
      background: colors.$color-white;
      border: none;
      box-shadow: settings.$box-shadow-focus-ring, 0 0 0 1px colors.$color-blue-300;
      caret-color: colors.$color-blue-600;
    }
  }

  .input-box__input:disabled {
    color: colors.$color-grey-300;
    background: colors.$color-grey-200;
    cursor: not-allowed;

    &::placeholder {
      color: colors.$color-grey-300;
    }
  }

  .input-box__input:disabled ~ .input-box__iconwrapper {
    color: colors.$color-grey-300;
  }
}

/**
 * color dark
 */
.input-box--color-dark {
  .input-box__input:placeholder-shown ~ .input-box__iconwrapper {
    color: colors.$color-white-50;
  }

  .input-box__label {
    color: colors.$color-white;
  }

  .input-box__wrapper {
    .input-box__iconwrapper {
      color: colors.$color-white;
    }

    .input-box__input {
      background: colors.$color-white-20;
      color: colors.$color-white;

      &::placeholder {
        color: colors.$color-white-50;
      }
    }

    &:focus-within {
      .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
        color: colors.$color-grey-700;
      }

      .input-box__input:not(:disabled) {
        background: colors.$color-white;
        box-shadow: settings.$box-shadow-border-blue-hidden, settings.$box-shadow-focus;
        color: colors.$color-grey-700;

        &::placeholder {
          color: colors.$color-grey-500;
        }
      }
    }

    &:hover {
      .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
        color: colors.$color-grey-700;
      }

      .input-box__input:not(:disabled) {
        background: colors.$color-white;
        box-shadow: settings.$box-shadow-border-blue;
        color: colors.$color-grey-700;

        &::placeholder {
          color: colors.$color-grey-500;
        }
      }
    }

    &:hover:focus-within {
      .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
        color: colors.$color-grey-700;
      }

      .input-box__input:not(:disabled) {
        background: colors.$color-white;
        box-shadow: settings.$box-shadow-border-blue, settings.$box-shadow-focus;
        color: colors.$color-grey-700;

        &::placeholder {
          color: colors.$color-grey-500;
        }
      }
    }

    .input-box__input:disabled {
      color: colors.$color-white-20;
      background: colors.$color-white-20;
      cursor: not-allowed;

      &::placeholder {
        color: colors.$color-white-20;
      }
    }

    .input-box__input:disabled ~ .input-box__iconwrapper {
      color: colors.$color-white-20;
    }
  }
}

/**
 * color ghost
 */
.input-box--color-ghost .input-box__wrapper {
  .input-box__input:placeholder-shown ~ .input-box__iconwrapper {
    color: colors.$color-grey-500;
  }

  .input-box__input {
    background: transparent;
    color: colors.$color-grey-900;

    @include typography.typography-16-medium(28px);

    &::placeholder {
      color: colors.$color-grey-500;
    }
  }

  &:focus-within {
    .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
      color: colors.$color-blue-600;
    }

    .input-box__input:not(:disabled) {
      box-shadow: settings.$box-shadow-border-blue-300, settings.$box-shadow-focus;
    }
  }

  &:hover {
    .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
      color: colors.$color-blue-600;
    }

    .input-box__input:not(:disabled) {
      box-shadow: settings.$box-shadow-border-grey-30;
    }
  }

  &:hover:focus-within {
    .input-box__input:not(:disabled) ~ .input-box__iconwrapper {
      color: colors.$color-blue-600;
    }

    .input-box__input:not(:disabled) {
      box-shadow: settings.$box-shadow-border-blue-300, settings.$box-shadow-focus;
    }
  }

  .input-box__input:disabled {
    color: colors.$color-grey-300;
    cursor: not-allowed;

    &::placeholder {
      color: colors.$color-grey-300;
    }
  }

  .input-box__input:disabled ~ .input-box__iconwrapper {
    color: colors.$color-grey-300;
  }
}

.input-box--error {
  .input-box__wrapper {
    border: 1px solid colors.$input-error;
  }
}

.input-box .error-container {
  margin: 0;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: 8px 16px;
}
</style>
